import { SelectInput, required, useTranslate } from 'react-admin';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { Palette } from '@/themes/themeOverrides/Palette';
import ServiceNewIcon from '@/assets/icons/service-new-icon.svg?react';
import ServiceFailedIcon from '@/assets/icons/service-failed-icon.svg?react';
import ServiceProgressIcon from '@/assets/icons/service-progress-icon.svg?react';

const baseBoxStyles = {
    display: 'flex',
    alignItems: 'center',
    boxShadow: 'none !important',
    background: 'transparent !important',
    borderRadius: '4px !important',
    overflow: 'visible !important',
    textWrap: 'auto',
};

const selectedBoxStyles = {
    '.Mui-selected &::before': {
        content: '""',
        position: 'absolute',
        top: '6px',
        left: '6px',
        translate: '-50%, -50%',
        width: '28px',
        height: '28px',
        backgroundColor: Palette.white,
        borderRadius: '4px',
        zIndex: 1,
        [`@media (max-width: ${breakpoints.values.sm}px)`]: {
            top: '10px',
        },
    },
};

const iconBoxStyles = {
    '& .MuiBox-root': {
        background: 'unset !important',
    },
};

const colorBoxStyles = {
    position: 'relative',
    width: '20px',
    minWidth: '20px',
    height: '20px',
    marginRight: '11px',
    zIndex: 1,
    overflow: 'visible',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
};

const outlinedColorBoxStyles = {
    borderRadius: '6px',
    outline: '2px solid',
    outlineOffset: '-2px',
};

const selectStyles = {
    '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center',
    },
    '& .MuiPaper-root': {
        marginTop: '2px !important',
    },
};

const serviceRequestStatusIconMap = {
    new: <ServiceNewIcon />,
    failedContact: <ServiceFailedIcon />,
    accepted: <ServiceProgressIcon />,
    pending: <ServiceProgressIcon />,
    inProgress: <ServiceProgressIcon />,
    fixed: <ServiceProgressIcon />,
    closed: <ServiceProgressIcon />,
};

const ChoiceOptionText = ({ choice, icon, className, translate }) => {
    const isOutlined = className && typeof className === 'string' && className.includes('select-with-icon--outlined');
    const translatedName = translate(choice.name, { _: choice.name });

    return (
        <Box
            sx={{
                ...baseBoxStyles,
                ...(!icon && !isOutlined && selectedBoxStyles),
                ...(icon && iconBoxStyles),
            }}
        >
            <Box
                className='color-box'
                sx={{
                    ...colorBoxStyles,
                    backgroundColor: `${choice.color || choice.id} !important`,
                    ...(!icon &&
                        isOutlined && {
                            ...outlinedColorBoxStyles,
                            outlineColor: choice.outlineColor,
                        }),
                }}
            >
                {icon}
            </Box>
            {translatedName}
        </Box>
    );
};

ChoiceOptionText.propTypes = {
    choice: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        color: PropTypes.string,
        outlineColor: PropTypes.string,
    }).isRequired,
    icon: PropTypes.element,
    className: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

const SelectOptionText = ({ choice, iconMap, serviceRequestStatusIconMap, className, translate }) => {
    const icon = iconMap.get(choice.id) || serviceRequestStatusIconMap[choice.id];
    return <ChoiceOptionText choice={choice} icon={icon} className={className} translate={translate} />;
};

SelectOptionText.propTypes = {
    choice: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        color: PropTypes.string,
        outlineColor: PropTypes.string,
    }).isRequired,
    iconMap: PropTypes.instanceOf(Map).isRequired,
    serviceRequestStatusIconMap: PropTypes.object.isRequired,
    className: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

const SelectWithIcon = ({ source, colorChoices, validate, label, className, disableValue, disabled, ...props }) => {
    const translate = useTranslate();
    const validation = validate || required(translate('common.validation.required'));
    const defaultLabel = label || translate('common.color');

    const iconMap = new Map(colorChoices.map(choice => [choice.id, choice.icon]));

    const cleanChoices = colorChoices.map(choice => Object.fromEntries(Object.entries(choice).filter(([key]) => key !== 'icon')));

    return (
        <SelectInput
            source={source}
            validate={validation}
            choices={cleanChoices}
            label={defaultLabel}
            optionValue='id'
            className={className}
            disableValue={disableValue}
            disabled={disabled}
            optionText={choice => (
                <SelectOptionText
                    choice={choice}
                    iconMap={iconMap}
                    serviceRequestStatusIconMap={serviceRequestStatusIconMap}
                    className={className}
                    translate={translate}
                />
            )}
            sx={selectStyles}
            fullWidth
            {...props}
        />
    );
};

SelectWithIcon.propTypes = {
    source: PropTypes.string.isRequired,
    colorChoices: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
            icon: PropTypes.element,
            color: PropTypes.string,
            outlineColor: PropTypes.string,
            shades: PropTypes.object,
        }),
    ).isRequired,
    validate: PropTypes.func,
    label: PropTypes.string,
    className: PropTypes.string,
    disableValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
};

export default SelectWithIcon;
